/**
 * Regression statistics for the trend analysis, shared by the server
 * (evaluation chart + trending-problem detection) and the client (Trends
 * page) so that both project the same way. The 95% prediction interval is the
 * one the original trends design specified and verified against Excel (see
 * client trends.spec.ts and server trendStats.test.ts):
 *
 *   (m * x0 + b) +/- t_025 * STEYX * sqrt(1 + 1/n + (x0 - x_mean)^2 / DEVSQ)
 */

/**
 * Since JavaScript's statistical ecosystem is woefully weak, here's a function that will give you a t score
 */
export function tScore(
  level: 0.1 | 0.05 | 0.025 | 0.01 | 0.005 | 0.001 | 0.0005,
  df: number
): number {
  const table: {
    [df: number]: {
      0.1: number;
      0.05: number;
      0.025: number;
      0.01: number;
      0.005: number;
      0.001: number;
      0.0005: number;
    };
  } = {
    1: {
      0.1: 3.078,
      0.05: 6.314,
      0.025: 12.706,
      0.01: 31.821,
      0.005: 63.656,
      0.001: 318.289,
      0.0005: 636.578,
    },
    2: {
      0.1: 1.886,
      0.05: 2.92,
      0.025: 4.303,
      0.01: 6.965,
      0.005: 9.925,
      0.001: 22.328,
      0.0005: 31.6,
    },
    3: {
      0.1: 1.638,
      0.05: 2.353,
      0.025: 3.182,
      0.01: 4.541,
      0.005: 5.841,
      0.001: 10.214,
      0.0005: 12.924,
    },
    4: {
      0.1: 1.533,
      0.05: 2.132,
      0.025: 2.776,
      0.01: 3.747,
      0.005: 4.604,
      0.001: 7.173,
      0.0005: 8.61,
    },
    5: {
      0.1: 1.476,
      0.05: 2.015,
      0.025: 2.571,
      0.01: 3.365,
      0.005: 4.032,
      0.001: 5.894,
      0.0005: 6.869,
    },
    6: {
      0.1: 1.44,
      0.05: 1.943,
      0.025: 2.447,
      0.01: 3.143,
      0.005: 3.707,
      0.001: 5.208,
      0.0005: 5.959,
    },
    7: {
      0.1: 1.415,
      0.05: 1.895,
      0.025: 2.365,
      0.01: 2.998,
      0.005: 3.499,
      0.001: 4.785,
      0.0005: 5.408,
    },
    8: {
      0.1: 1.397,
      0.05: 1.86,
      0.025: 2.306,
      0.01: 2.896,
      0.005: 3.355,
      0.001: 4.501,
      0.0005: 5.041,
    },
    9: {
      0.1: 1.383,
      0.05: 1.833,
      0.025: 2.262,
      0.01: 2.821,
      0.005: 3.25,
      0.001: 4.297,
      0.0005: 4.781,
    },
    10: {
      0.1: 1.372,
      0.05: 1.812,
      0.025: 2.228,
      0.01: 2.764,
      0.005: 3.169,
      0.001: 4.144,
      0.0005: 4.587,
    },
    11: {
      0.1: 1.363,
      0.05: 1.796,
      0.025: 2.201,
      0.01: 2.718,
      0.005: 3.106,
      0.001: 4.025,
      0.0005: 4.437,
    },
    12: {
      0.1: 1.356,
      0.05: 1.782,
      0.025: 2.179,
      0.01: 2.681,
      0.005: 3.055,
      0.001: 3.93,
      0.0005: 4.318,
    },
    13: {
      0.1: 1.35,
      0.05: 1.771,
      0.025: 2.16,
      0.01: 2.65,
      0.005: 3.012,
      0.001: 3.852,
      0.0005: 4.221,
    },
    14: {
      0.1: 1.345,
      0.05: 1.761,
      0.025: 2.145,
      0.01: 2.624,
      0.005: 2.977,
      0.001: 3.787,
      0.0005: 4.14,
    },
    15: {
      0.1: 1.341,
      0.05: 1.753,
      0.025: 2.131,
      0.01: 2.602,
      0.005: 2.947,
      0.001: 3.733,
      0.0005: 4.073,
    },
    16: {
      0.1: 1.337,
      0.05: 1.746,
      0.025: 2.12,
      0.01: 2.583,
      0.005: 2.921,
      0.001: 3.686,
      0.0005: 4.015,
    },
    17: {
      0.1: 1.333,
      0.05: 1.74,
      0.025: 2.11,
      0.01: 2.567,
      0.005: 2.898,
      0.001: 3.646,
      0.0005: 3.965,
    },
    18: {
      0.1: 1.33,
      0.05: 1.734,
      0.025: 2.101,
      0.01: 2.552,
      0.005: 2.878,
      0.001: 3.61,
      0.0005: 3.922,
    },
    19: {
      0.1: 1.328,
      0.05: 1.729,
      0.025: 2.093,
      0.01: 2.539,
      0.005: 2.861,
      0.001: 3.579,
      0.0005: 3.883,
    },
    20: {
      0.1: 1.325,
      0.05: 1.725,
      0.025: 2.086,
      0.01: 2.528,
      0.005: 2.845,
      0.001: 3.552,
      0.0005: 3.85,
    },
    21: {
      0.1: 1.323,
      0.05: 1.721,
      0.025: 2.08,
      0.01: 2.518,
      0.005: 2.831,
      0.001: 3.527,
      0.0005: 3.819,
    },
    22: {
      0.1: 1.321,
      0.05: 1.717,
      0.025: 2.074,
      0.01: 2.508,
      0.005: 2.819,
      0.001: 3.505,
      0.0005: 3.792,
    },
    23: {
      0.1: 1.319,
      0.05: 1.714,
      0.025: 2.069,
      0.01: 2.5,
      0.005: 2.807,
      0.001: 3.485,
      0.0005: 3.768,
    },
    24: {
      0.1: 1.318,
      0.05: 1.711,
      0.025: 2.064,
      0.01: 2.492,
      0.005: 2.797,
      0.001: 3.467,
      0.0005: 3.745,
    },
    25: {
      0.1: 1.316,
      0.05: 1.708,
      0.025: 2.06,
      0.01: 2.485,
      0.005: 2.787,
      0.001: 3.45,
      0.0005: 3.725,
    },
    26: {
      0.1: 1.315,
      0.05: 1.706,
      0.025: 2.056,
      0.01: 2.479,
      0.005: 2.779,
      0.001: 3.435,
      0.0005: 3.707,
    },
    27: {
      0.1: 1.314,
      0.05: 1.703,
      0.025: 2.052,
      0.01: 2.473,
      0.005: 2.771,
      0.001: 3.421,
      0.0005: 3.689,
    },
    28: {
      0.1: 1.313,
      0.05: 1.701,
      0.025: 2.048,
      0.01: 2.467,
      0.005: 2.763,
      0.001: 3.408,
      0.0005: 3.674,
    },
    29: {
      0.1: 1.311,
      0.05: 1.699,
      0.025: 2.045,
      0.01: 2.462,
      0.005: 2.756,
      0.001: 3.396,
      0.0005: 3.66,
    },
    30: {
      0.1: 1.31,
      0.05: 1.697,
      0.025: 2.042,
      0.01: 2.457,
      0.005: 2.75,
      0.001: 3.385,
      0.0005: 3.646,
    },
    60: {
      0.1: 1.296,
      0.05: 1.671,
      0.025: 2.0,
      0.01: 2.39,
      0.005: 2.66,
      0.001: 3.232,
      0.0005: 3.46,
    },
    120: {
      0.1: 1.289,
      0.05: 1.658,
      0.025: 1.98,
      0.01: 2.358,
      0.005: 2.617,
      0.001: 3.16,
      0.0005: 3.373,
    },
    1000: {
      0.1: 1.282,
      0.05: 1.646,
      0.025: 1.962,
      0.01: 2.33,
      0.005: 2.581,
      0.001: 3.098,
      0.0005: 3.3,
    },
    Infinity: {
      0.1: 1.282,
      0.05: 1.645,
      0.025: 1.96,
      0.01: 2.326,
      0.005: 2.576,
      0.001: 3.091,
      0.0005: 3.291,
    },
  };
  for (const key in table) {
    if (typeof key === "string") {
      if (df <= parseInt(key)) {
        return table[key][level];
      }
    }
  }
  return table[Infinity][level];
}

/** modified version of Excel STEYX given that a regression has already been done */
export function steyx(yrange: number[], yfit: number[]): number {
  const sse: number = yrange
    .map((yval, index) => (yval - yfit[index]) ** 2)
    .reduce((acc, curr) => acc + curr, 0);
  return Math.sqrt((1 / (yrange.length - 2)) * sse);
}

/** see Excel DEVSQ */
export function devsq(x_values: number[]): number {
  const x_mean = mean(x_values);
  const sumSquaredDeviation = x_values
    .map((x) => (x - x_mean) ** 2)
    .reduce((acc, curr) => acc + curr, 0);
  return sumSquaredDeviation;
}

function mean(values: number[]): number {
  return values.reduce((acc, curr) => acc + curr, 0) / values.length;
}

/**
 * Half-width of the 95% prediction interval of the linear fit at x0:
 * t_025(n-2) * STEYX * sqrt(1 + 1/n + (x0 - x_mean)^2 / DEVSQ(x)). Infinity
 * when the fit is not determined (fewer than 3 points, or all x equal), so
 * that no projection can be called a problem.
 */
export function predictionHalfWidth(
  x: number[],
  y: number[],
  y_fit: number[]
): (x0: number) => number {
  const n = x.length;
  if (n <= 2) return () => Infinity;
  const dev = devsq(x);
  if (dev === 0) return () => Infinity;
  const s = steyx(y, y_fit);
  const x_mean = mean(x);
  const t = tScore(0.025, n - 2);
  return (x0: number) =>
    t * s * Math.sqrt(1 + 1 / n + (x0 - x_mean) ** 2 / dev);
}
